import { AppState, Auth0Provider, User } from '@auth0/auth0-react';
import { isProductionEnvironment } from '@luminovo/commons';
import { CenteredSpinner } from '@luminovo/design-system';
import React, { type JSX } from 'react';

import { getAuth0AuthorizationParams } from '../hooks/useAuth';
// Auth0 parameters are passed as environment variables and are placed in window
// https://jakobzanker.de/blog/inject-environment-variables-into-a-react-app-docker-on-runtime/
declare global {
    interface Window {
        AUTH0_AUTHORITY: string;
        AUTH0_CLIENT_ID: string;
    }
}

export function AuthProvider({
    children,
    auth0OrgId,
    history,
    onRedirectCallback: handleRedirectCallback = () => {},
}: {
    children: React.ReactNode;
    auth0OrgId: string | undefined;
    /** Replaces the URL after Auth0's callback: the app's `router.history`. Only `replace`, so no router dependency. */
    history: {
        replace: (path: string) => void;
    };
    onRedirectCallback: (appState?: AppState, user?: User) => void;
}): JSX.Element {
    // A function that routes the user to the right place
    // after login
    const onRedirectCallback = (appState?: AppState, user?: User) => {
        handleRedirectCallback(appState, user);
        history.replace(sameOriginPath(appState?.returnTo));
    };
    // A pen test flagged localstorage tokens, so production keeps them in memory; its custom Auth0 domain helps too.
    // Elsewhere a reload would then lose the session, too disruptive in development and on trail/preview envs.
    const cacheLocation = isProductionEnvironment() ? 'memory' : 'localstorage';

    return (
        <React.Suspense fallback={<CenteredSpinner />}>
            <Auth0Provider
                domain={window.AUTH0_AUTHORITY}
                clientId={window.AUTH0_CLIENT_ID}
                onRedirectCallback={onRedirectCallback}
                authorizationParams={{ ...getAuth0AuthorizationParams(), organization: auth0OrgId }}
                cacheLocation={cacheLocation}
                useRefreshTokens={true}
                useRefreshTokensFallback={true}
            >
                {children}
            </Auth0Provider>
        </React.Suspense>
    );
}

/** `returnTo` can come from any link, and `replaceState` to another origin throws in the SDK's callback. */
function sameOriginPath(returnTo: unknown): string {
    if (typeof returnTo !== 'string') {
        return '/';
    }
    try {
        const url = new URL(returnTo, window.location.origin);
        const path = url.pathname + url.search + url.hash;
        // A path starting `//` names another host, and `/.//host` resolves to one on this origin.
        return url.origin === window.location.origin && !path.startsWith('//') ? path : '/';
    } catch {
        return '/';
    }
}
